import React, { useState } from 'react';
import {
  ComponentDescriptor,
  useFields,
  getRendererFromDescriptor,
  useForms,
} from '@kizi/comparator-core';
import { P, Div } from '@kizi/core-components';
import { Fields, ExtraProps } from '../PersonalData';
import { PersonalDataHandler } from './PersonalDataHandler';

export interface InnerContentProps {
  title: string;
  fields: Fields;
  templateFields: any;
  forms: string[];
  extra?: ExtraProps;
}
export interface InnerContentWrapperProps {
  title: string;
  formFields: Fields;
  templateFields: Fields;
  forms: string[];
  extra?: ExtraProps;
}

const Flex = (props) => <Div display="flex" width="100%" pt={2} {...props} />;
const Paragraph = (props) => (
  <P variant="footnoteRegular" mt={1} {...props}>
    {props.children}
  </P>
);
export const InnerContent: React.FC<InnerContentProps> = ({
  title,
  fields,
  templateFields,
  forms,
  extra,
}) => {
  const _forms = useForms(forms) ?? [];

  const [originalValues] = useState(
    _forms.map((form) => form?.state.context.fields)
  );

  const _fields = fields.map((field, index) => (
    <Div key={`field-paragraph-${index}`} variant="personalDataFieldContainer">
      {field}
    </Div>
  ));

  const extraHeaderContent =
    extra?.header && typeof extra?.header === 'string' ? (
      <Paragraph variant="footnoteImportant">{extra.header}</Paragraph>
    ) : (
      getRendererFromDescriptor(extra?.header as ComponentDescriptor)
    );

  const extraContainer = extra ? (
    <Flex
      flexDirection={'column'}
      borderTop="1px solid"
      borderColor="second.1"
      mt={3}
    >
      {extraHeaderContent}
      {extra?.body && <Paragraph> {extra.body}</Paragraph>}
    </Flex>
  ) : null;

  const onCancel = () => {
    let i = 0;
    for (let form of _forms) {
      let sendEvents: any = [];
      for (let field of originalValues[i]) {
        sendEvents.push({
          type: 'CHANGE',
          target: { name: field.name, value: field.value },
        });
      }
      form.send(sendEvents);
      i++;
    }
  };

  return (
    <PersonalDataHandler
      title={title}
      formFields={_fields}
      forms={forms}
      dataTemplate={templateFields}
      onCancel={onCancel}
      editData={true}
      extraContent={extraContainer}
    />
  );
};

export const InnerContentWrapper: React.FC<InnerContentWrapperProps> = ({
  title,
  formFields,
  templateFields,
  forms,
  extra,
}) => {
  const fields: any[] = [];

  const filterByStringFields = (_fields) =>
    _fields.filter((f: ComponentDescriptor | string) => typeof f === 'string');

  const filterByObjectFields = (_fields) =>
    _fields.filter((f: ComponentDescriptor | string) => typeof f !== 'string');

  const formStringFields = filterByStringFields(formFields);

  const fieldsFromContext = useFields(formStringFields);

  for (const element of fieldsFromContext) {
    if (element !== undefined) {
      const { errors, label, name, type, value, onChange, active } = element;
      const reactElement = getRendererFromDescriptor({
        type: type as string,
        props: {
          name,
          value,
          active,
          onChange,
          _label: label,
          _error: errors,
        },
      });
      if (reactElement !== null && active) {
        fields.push(reactElement);
      }
    }
  }

  const objectFields = filterByObjectFields(formFields);

  for (const element of objectFields) {
    const reactElement = getRendererFromDescriptor(element);
    fields.push(reactElement);
  }

  const objectTemplateFields = filterByObjectFields(templateFields);
  const _templateFields: any[] = [];
  for (const field of objectTemplateFields) {
    const reactElement = getRendererFromDescriptor(field);
    _templateFields.push(reactElement);
  }

  return (
    <InnerContent
      title={title}
      fields={fields}
      templateFields={_templateFields}
      forms={forms}
      extra={extra}
    />
  );
};
